import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Badge, ProgressBar, ListGroup, Button, Nav } from 'react-bootstrap';
import ReactFlow, {
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
  MarkerType,
  Position,
  Handle
} from 'reactflow';
import 'reactflow/dist/style.css';
import { FaPlay, FaCheckCircle, FaTimesCircle, FaSpinner, FaClock } from 'react-icons/fa';

interface WorkflowStep {
  id: string;
  agent: string;
  action: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  startTime?: Date;
  endTime?: Date;
  input?: any;
  output?: any;
  logs?: string[];
}

interface Workflow {
  id: string;
  name: string;
  status: 'active' | 'completed' | 'failed';
  agents: string[];
  steps: WorkflowStep[];
  startTime: Date;
  endTime?: Date;
  duration?: number;
  progress: number;
}

interface WorkflowVisualizerProps {
  wsConnection: WebSocket | null;
}

const CustomAgentNode: React.FC<{ data: any }> = ({ data }) => {
  const getStatusColor = () => {
    switch (data.status) {
      case 'completed': return '#4caf50';
      case 'in_progress': return '#2196f3';
      case 'failed': return '#f44336';
      default: return '#757575';
    }
  };

  return (
    <div
      style={{
        padding: '10px 20px',
        borderRadius: '8px',
        backgroundColor: '#1a1a1a',
        border: `2px solid ${getStatusColor()}`,
        color: 'white',
        minWidth: '150px',
        textAlign: 'center'
      }}
    >
      <Handle type="target" position={Position.Left} />
      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{data.label}</div>
      <div style={{ fontSize: '0.8em', color: '#ccc' }}>{data.agent}</div>
      {data.status === 'in_progress' && <FaSpinner className="fa-spin mt-2" />}
      {data.status === 'completed' && <FaCheckCircle className="mt-2" style={{ color: '#4caf50' }} />}
      {data.status === 'failed' && <FaTimesCircle className="mt-2" style={{ color: '#f44336' }} />}
      <Handle type="source" position={Position.Right} />
    </div>
  );
};

const nodeTypes = {
  agentNode: CustomAgentNode
};

const WorkflowVisualizer: React.FC<WorkflowVisualizerProps> = ({ wsConnection }) => {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<string | null>(null);
  const [selectedStep, setSelectedStep] = useState<WorkflowStep | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Mock data for demonstration
  useEffect(() => {
    const mockWorkflows: Workflow[] = [
      {
        id: 'wf-001',
        name: 'Document Processing Pipeline',
        status: 'active',
        agents: ['OTIS', 'DocMaster-Σ', 'ComplianceAnalyst-Φ'],
        steps: [
          {
            id: 'step-1',
            agent: 'OTIS',
            action: 'Receive document',
            status: 'completed',
            startTime: new Date(Date.now() - 300000),
            endTime: new Date(Date.now() - 240000)
          },
          {
            id: 'step-2',
            agent: 'DocMaster-Σ',
            action: 'Process and extract',
            status: 'in_progress',
            startTime: new Date(Date.now() - 240000),
            logs: [
              'Analyzing document structure...',
              'Extracting metadata...',
              'Processing page 3 of 5...'
            ]
          },
          {
            id: 'step-3',
            agent: 'ComplianceAnalyst-Φ',
            action: 'Compliance review',
            status: 'pending'
          }
        ],
        startTime: new Date(Date.now() - 300000),
        progress: 45
      },
      {
        id: 'wf-002',
        name: 'Deployment Workflow',
        status: 'completed',
        agents: ['SupervisorPrime-Ω', 'DeployBot-Alpha', 'SecureWatch-Alpha'],
        steps: [
          {
            id: 'step-4',
            agent: 'SupervisorPrime-Ω',
            action: 'Initiate deployment',
            status: 'completed'
          },
          {
            id: 'step-5',
            agent: 'DeployBot-Alpha',
            action: 'Deploy to production',
            status: 'completed'
          },
          {
            id: 'step-6',
            agent: 'SecureWatch-Alpha',
            action: 'Security scan',
            status: 'completed'
          }
        ],
        startTime: new Date(Date.now() - 3600000),
        endTime: new Date(Date.now() - 1800000),
        duration: 1800000,
        progress: 100
      }
    ];

    setWorkflows(mockWorkflows);
    if (mockWorkflows.length > 0) {
      setSelectedWorkflow(mockWorkflows[0].id);
    }
  }, []);

  useEffect(() => {
    if (!wsConnection) return;

    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'workflow_update') {
          setWorkflows(prev => {
            const index = prev.findIndex(w => w.id === data.workflow.id);
            if (index >= 0) {
              const updated = [...prev];
              updated[index] = data.workflow;
              return updated;
            } else {
              return [...prev, data.workflow];
            }
          });
        }
      } catch (error) {
        console.error('Error processing workflow update:', error);
      }
    };

    wsConnection.addEventListener('message', handleMessage);
    return () => {
      wsConnection.removeEventListener('message', handleMessage);
    };
  }, [wsConnection]);

  useEffect(() => {
    if (!selectedWorkflow) return;

    const workflow = workflows.find(w => w.id === selectedWorkflow);
    if (!workflow) return;

    // Generate nodes and edges for the workflow
    const newNodes: Node[] = [
      {
        id: 'start',
        type: 'input',
        data: { label: 'Start' },
        position: { x: 0, y: 100 },
        style: { backgroundColor: '#4caf50', color: 'white' }
      }
    ];

    const newEdges: Edge[] = [];
    let prevNodeId = 'start';

    workflow.steps.forEach((step, index) => {
      const nodeId = `node-${step.id}`;
      newNodes.push({
        id: nodeId,
        type: 'agentNode',
        data: {
          label: step.action,
          agent: step.agent,
          status: step.status
        },
        position: { x: 200 + index * 250, y: 100 }
      });

      newEdges.push({
        id: `edge-${prevNodeId}-${nodeId}`,
        source: prevNodeId,
        target: nodeId,
        type: 'smoothstep',
        animated: step.status === 'in_progress',
        markerEnd: {
          type: MarkerType.ArrowClosed,
        },
        style: {
          stroke: step.status === 'completed' ? '#4caf50' : '#757575',
          strokeWidth: 2
        }
      });

      prevNodeId = nodeId;
    });

    // Add end node
    const endNodeId = 'end';
    newNodes.push({
      id: endNodeId,
      type: 'output',
      data: { label: 'End' },
      position: { x: 200 + workflow.steps.length * 250, y: 100 },
      style: { 
        backgroundColor: workflow.status === 'completed' ? '#4caf50' : '#757575', 
        color: 'white' 
      }
    });

    newEdges.push({
      id: `edge-${prevNodeId}-${endNodeId}`,
      source: prevNodeId,
      target: endNodeId,
      type: 'smoothstep',
      markerEnd: {
        type: MarkerType.ArrowClosed,
      },
      style: {
        stroke: workflow.status === 'completed' ? '#4caf50' : '#757575',
        strokeWidth: 2
      }
    });

    setNodes(newNodes);
    setEdges(newEdges);
  }, [selectedWorkflow, workflows]);

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    if (node.type === 'agentNode' && selectedWorkflow) {
      const workflow = workflows.find(w => w.id === selectedWorkflow);
      const step = workflow?.steps.find(s => `node-${s.id}` === node.id);
      if (step) {
        setSelectedStep(step);
      }
    }
  }, [selectedWorkflow, workflows]);

  const currentWorkflow = workflows.find(w => w.id === selectedWorkflow);

  return (
    <Row className="h-100">
      <Col md={8}>
        <Card className="h-100">
          <Card.Header className="bg-dark text-white">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Workflow Visualization</h5>
              {currentWorkflow && (
                <Badge bg={currentWorkflow.status === 'active' ? 'primary' : 
                          currentWorkflow.status === 'completed' ? 'success' : 'danger'}>
                  {currentWorkflow.status.toUpperCase()}
                </Badge>
              )}
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <Nav variant="tabs" className="px-3 pt-2">
              {workflows.map(workflow => (
                <Nav.Item key={workflow.id}>
                  <Nav.Link
                    active={selectedWorkflow === workflow.id}
                    onClick={() => setSelectedWorkflow(workflow.id)}
                  >
                    {workflow.name}
                    <Badge 
                      bg="secondary" 
                      className="ms-2"
                      pill
                    >
                      {workflow.progress}%
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
            
            <div style={{ height: '500px' }}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={onNodeClick}
                nodeTypes={nodeTypes}
                fitView
              >
                <Background />
                <Controls />
                <MiniMap 
                  nodeColor={(node: any) => {
                    if (node.type === 'agentNode') {
                      switch (node.data.status) {
                        case 'completed': return '#4caf50';
                        case 'in_progress': return '#2196f3';
                        case 'failed': return '#f44336';
                        default: return '#757575';
                      }
                    }
                    return '#999';
                  }}
                />
              </ReactFlow>
            </div>

            {currentWorkflow && (
              <div className="p-3 border-top">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>Overall Progress</span>
                  <span>{currentWorkflow.progress}%</span>
                </div>
                <ProgressBar 
                  now={currentWorkflow.progress} 
                  variant={currentWorkflow.status === 'completed' ? 'success' : 
                          currentWorkflow.status === 'failed' ? 'danger' : 'primary'}
                  animated={currentWorkflow.status === 'active'}
                />
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      <Col md={4}>
        <Card className="h-100">
          <Card.Header className="bg-dark text-white">
            <h6 className="mb-0">Workflow Details</h6>
          </Card.Header>
          <Card.Body style={{ overflowY: 'auto' }}>
            {selectedStep ? (
              <>
                <h6>Current Step</h6>
                <ListGroup className="mb-3">
                  <ListGroup.Item>
                    <div className="d-flex justify-content-between">
                      <strong>Agent:</strong>
                      <span>{selectedStep.agent}</span>
                    </div>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <div className="d-flex justify-content-between">
                      <strong>Action:</strong>
                      <span>{selectedStep.action}</span>
                    </div>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <div className="d-flex justify-content-between">
                      <strong>Status:</strong>
                      <Badge bg={
                        selectedStep.status === 'completed' ? 'success' :
                        selectedStep.status === 'in_progress' ? 'primary' :
                        selectedStep.status === 'failed' ? 'danger' : 'secondary'
                      }>
                        {selectedStep.status}
                      </Badge>
                    </div>
                  </ListGroup.Item>
                  {selectedStep.startTime && (
                    <ListGroup.Item>
                      <div className="d-flex justify-content-between">
                        <strong>Started:</strong>
                        <span>{new Date(selectedStep.startTime).toLocaleTimeString()}</span>
                      </div>
                    </ListGroup.Item>
                  )}
                </ListGroup>

                {selectedStep.logs && selectedStep.logs.length > 0 && (
                  <>
                    <h6>Logs</h6>
                    <div className="bg-dark text-light p-2 rounded" style={{ fontSize: '0.85rem' }}>
                      {selectedStep.logs.map((log, index) => (
                        <div key={index} className="mb-1">
                          <FaClock size={10} className="me-2" />
                          {log}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : currentWorkflow ? (
              <>
                <h6>Workflow Summary</h6>
                <ListGroup>
                  <ListGroup.Item>
                    <div className="d-flex justify-content-between">
                      <strong>Name:</strong>
                      <span>{currentWorkflow.name}</span>
                    </div>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <div className="d-flex justify-content-between">
                      <strong>Started:</strong>
                      <span>{new Date(currentWorkflow.startTime).toLocaleTimeString()}</span>
                    </div>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <div className="d-flex justify-content-between">
                      <strong>Duration:</strong>
                      <span>
                        {currentWorkflow.duration 
                          ? `${Math.round(currentWorkflow.duration / 60000)} minutes`
                          : 'In progress...'}
                      </span>
                    </div>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <div className="d-flex justify-content-between">
                      <strong>Agents Involved:</strong>
                      <span>{currentWorkflow.agents.length}</span>
                    </div>
                  </ListGroup.Item>
                </ListGroup>

                <h6 className="mt-3">Steps Overview</h6>
                <ListGroup>
                  {currentWorkflow.steps.map(step => (
                    <ListGroup.Item 
                      key={step.id}
                      action
                      onClick={() => setSelectedStep(step)}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <strong>{step.agent}</strong>
                        <div className="text-muted small">{step.action}</div>
                      </div>
                      {step.status === 'completed' && <FaCheckCircle className="text-success" />}
                      {step.status === 'in_progress' && <FaSpinner className="text-primary fa-spin" />}
                      {step.status === 'failed' && <FaTimesCircle className="text-danger" />}
                      {step.status === 'pending' && <FaClock className="text-secondary" />}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </>
            ) : (
              <div className="text-center text-muted py-5">
                Select a workflow to view details
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default WorkflowVisualizer;